import { createSelector } from 'reselect';
import { PROJECT_ACTIVE } from '@atlassian/jira-issue-shared-types/src/common/types/project-type.tsx';
import { SUBTASKS } from '@atlassian/jira-issue-view-configurations/src/index.tsx';
import {
	isMobileSelector,
	isBitbucketSelector,
	isServiceDeskSelector,
} from '../common/state/selectors/context-selector.tsx';
import { childIssuesLimitUrlSelector } from '../common/state/selectors/field-selector.tsx';
import {
	entitiesSelector,
	isSimplifiedProjectSelector,
	projectSelector,
} from '../common/state/selectors/issue-selector.tsx';
import { permissionsSelector } from '../common/state/selectors/permissions-selector.tsx';
import { hasChildrenIssuesSelector } from '../issue-field/state/selectors/children-issue-selector.tsx';
import { isIssueBaseLevel } from '../issue-field/state/selectors/hierarchy-level-selector.tsx';
import { issueTypeSelector } from './breadcrumbs-selector.tsx';
import { projectHasSubtaskValidatorsSelector } from './project-validators-selector.tsx';
import {
	isSubtaskTypesPresentSelector,
	hasUnsupportedTypeSelector,
} from './subtask-types-selector.tsx';

export const isSubtaskSelector = createSelector(
	issueTypeSelector,
	(issueType) => !!(issueType && issueType.subtask),
);

export const subtaskEntitiesSelector = createSelector(
	entitiesSelector,
	(entities) => entities.subtasks || [],
);

// this selector when child issues is over 100 issues is a bit inaccurate
// due to AGG changes when over 100 child issues, we do not know the child issue panel types
// so when we are over the limit (portfolio > 100) or (subtasks + portfolio > 100)
// we will default the field to subtask. This means the scenario (portfolio > 100) will default
// the field to subtasks even though there is no subtasks.
export const hasSubtasksSelector = createSelector(
	subtaskEntitiesSelector,
	childIssuesLimitUrlSelector,
	(subtasks, getChildIssuesLimitUrl) =>
		subtasks.length > 0 || Boolean(getChildIssuesLimitUrl(SUBTASKS)),
);

export const hasChildIssuesOrSubtasksSelector = createSelector(
	isSimplifiedProjectSelector,
	isIssueBaseLevel,
	hasChildrenIssuesSelector,
	hasSubtasksSelector,
	(
		isSimplifiedProject: boolean,
		issueBaseLevel: boolean,
		hasChildrenIssues: boolean,
		hasSubtasks: boolean,
	): boolean => (isSimplifiedProject ? issueBaseLevel && hasChildrenIssues : hasSubtasks),
);

export const shouldUseFallbackSubtaskDialogSelector = createSelector(
	hasUnsupportedTypeSelector,
	projectHasSubtaskValidatorsSelector,
	(hasRequiredFields, hasSubtaskValidators) => hasRequiredFields || hasSubtaskValidators,
);

// Deprecated. Use jira-settings-hook instead if possible
export const isSubtaskEnabledSelector = createSelector(
	entitiesSelector,
	({ jiraSettings }) => jiraSettings?.subtasksConfiguration?.enabled,
);

export const canCreateSubtaskSelector = createSelector(
	permissionsSelector,
	isSubtaskSelector,
	isSubtaskTypesPresentSelector,
	shouldUseFallbackSubtaskDialogSelector,
	isMobileSelector,
	isBitbucketSelector,
	isServiceDeskSelector,
	isSimplifiedProjectSelector,
	isSubtaskEnabledSelector,
	projectSelector,
	(
		{ canCreateSubtask },
		isSubtask,
		typesPresent,
		shouldUseFallbackDialog,
		isMobile,
		isBitbucket,
		isServicedesk,
		isSimplified,
		subtasksEnabled,
		project,
	) => {
		const shouldHideForMobileOrBitbucket = shouldUseFallbackDialog
			? isMobile || isBitbucket
			: false;

		const isProjectActive = (project?.projectStatus ?? PROJECT_ACTIVE) === PROJECT_ACTIVE;

		const shouldShowSubtask = isServicedesk ? !isSimplified : true;

		return !!(
			canCreateSubtask &&
			!isSubtask &&
			typesPresent &&
			!shouldHideForMobileOrBitbucket &&
			shouldShowSubtask &&
			subtasksEnabled &&
			isProjectActive
		);
	},
);
